/**
 * World state hashing.
 *
 * `hashWorld` covers exactly the state that must be identical between two runs
 * of the same seed and input log. Derived caches (spatial hash, flow fields, the
 * per-tick event list) are deliberately excluded — they are pure functions of
 * this state, and including them would only make failures harder to read.
 */

import { Hasher } from '../core/hash.ts';
import { statsOf, type World } from './world.ts';
import { RESOURCE_KEYS } from './types.ts';
import { hashCampaign } from './campaign/hash.ts';
import { armies } from './battle.ts';

export function hashWorld(w: World): number {
  const h = new Hasher();
  h.u32(w.tick);
  h.u32(w.rng.s);
  h.u32(w.rngDirector.s);

  for (const k of RESOURCE_KEYS) h.num(w.res[k]);
  h.num(w.resCap);
  h.u32(w.phase);
  h.num(w.phaseT);
  h.u32(w.wave);
  h.u32(w.age);
  // The climb. Both fields decide what the director spends against you and when
  // the Age turns, so a world mid-research is not the same world as one that
  // never started.
  h.u32(w.researchingAge);
  h.num(w.ageResearch);
  // Whether the Coin of the Ferry has already been spent this Siege — the
  // difference between a Hearth that survives the next killing blow and one
  // that does not.
  h.bool(w.hearthSaved);
  /**
   * Who these people are, and the pool they may or may not be bounded by.
   *
   * `raceId` is about as behaviour-defining as a string gets: it selects the
   * muster binding, the economy mask the storage clamp reads every tick, the two
   * passives folded into `mods` at creation and which fourth column of the
   * Academy exists. `leaderId` picks two more passives on top of it. Two worlds
   * agreeing on every number and disagreeing here are two different games.
   *
   * `mana` and `manaMax` are hashed for the same reason `pop` and `popCap` are:
   * both are recomputed from live state every tick, and a field that is
   * recomputed is still the field the next decision is taken against. A world
   * that hashes them and one that does not are indistinguishable right up until
   * a save is loaded and a company is refused.
   */
  h.str(w.raceId);
  h.str(w.leaderId);
  h.num(w.pop);
  h.num(w.popCap);
  h.num(w.mana);
  h.num(w.manaMax);
  /**
   * The other two bindings that are carried as state rather than derived.
   *
   * `consecratedNames` and `thralls` refuse a muster exactly the way `manaMax`
   * does, and `nameWork` decides *when* the next name lands — so two Vigils a
   * second apart in the scriptorium are two worlds whose next decision differs,
   * and a digest that could not tell them apart would call a replay clean that
   * had already forked. Hashed for every race and written by two, which is the
   * same arrangement `mana` has and for the same reason: a branch is cheaper
   * than a conditional hash, and a conditional hash is a determinism bug the
   * first time somebody grants a thrall to the wrong people.
   */
  h.num(w.consecratedNames);
  h.num(w.nameWork);
  h.num(w.thralls);
  /**
   * The age bands. As behaviour-defining as `pop` itself: `assignJobs` sizes
   * the worker pool off them, `musterRoom` refuses a company the town has not
   * grown, and `stepDemography` kills off the grave debt — so two worlds that
   * agree on every head but disagree on which of them are grown staff
   * different fields, raise different armies and bury on different days.
   * `grave` is the same argument `nameWork` is: a fraction of a pending event,
   * and the reason a replayed save buries on the same tick.
   */
  h.num(w.demography.children);
  h.num(w.demography.elders);
  h.num(w.demography.grave);
  h.num(w.morale);
  h.u32(w.taxPolicy);
  h.num(w.arrears);
  h.num(w.fedFraction);
  h.num(w.breadShare);
  // How many kinds of food last tick's appetite was met from. Read by
  // `moraleTarget` and by the Hearth's growth, so two worlds that disagree
  // here diverge on the next villager born.
  h.num(w.dietVariety);
  h.num(w.will);
  h.num(w.willMax);
  h.i32(w.hearth);
  // Whose walls these are decides who may walk through the gates, who may swing
  // at the stone and which way both flow fields run. See `World.wallTeam`.
  h.i32(w.wallTeam);
  h.bool(w.gameOver);
  h.str(w.causeOfDeath);
  /**
   * Three counters that read like bookkeeping and are not.
   *
   * `rubbleCount` gates the whole idle-villager branch in `stepVillagers` —
   * hands only go looking for a pile when it says there is one, so a world that
   * has miscounted its rubble puts its workforce somewhere else entirely.
   * `victoryWave` is the latch that stops the spoils being offered twice for
   * the same wave. `nextCompanyId` is the id the next company will be given and
   * the salt its name is drawn from, so two worlds that disagree here raise
   * differently-numbered companies from the same order.
   */
  h.num(w.rubbleCount);
  h.u32(w.victoryWave);
  h.u32(w.nextCompanyId);

  // Map: only the layers the simulation mutates.
  h.arrU32(w.map.wall);
  h.arrNum(w.map.wallHp);
  // Was safe to omit only for as long as `mods.wallHp` was dead: every wall was
  // built to the same number, so max health was a function of tier alone. Now
  // that a tech can change what a section is built to, two settlements can
  // differ in max health while matching on current health, and repair rates
  // and breach thresholds diverge from there.
  h.arrNum(w.map.wallMaxHp);
  h.arrU32(w.map.wallTier);
  h.arrU32(w.map.terrain);
  h.arrU32(w.map.rubble);
  h.arrNum(w.map.rubbleWork);
  const nb = w.map.building;
  h.u32(nb.length);
  for (let i = 0; i < nb.length; i++) h.i32(nb[i]);

  // Units.
  const u = w.units;
  h.u32(u.slots.high);
  h.u32(u.slots.count);
  for (let i = 0; i < u.slots.high; i++) {
    h.u32(u.slots.alive[i]);
    if (u.slots.alive[i] === 0) continue;
    h.u32(u.slots.gen[i]);
    h.u32(u.typeId[i]);
    h.u32(u.team[i]);
    h.num(u.x[i]);
    h.num(u.y[i]);
    h.num(u.vx[i]);
    h.num(u.vy[i]);
    h.num(u.facing[i]);
    h.num(u.hp[i]);
    h.num(u.maxHp[i]);
    h.num(u.morale[i]);
    h.u32(u.state[i]);
    h.num(u.stateT[i]);
    h.num(u.attackCd[i]);
    h.num(u.swing[i]);
    h.u32(u.flags[i]);
    h.i32(u.companyId[i]);
    h.i32(u.slotIdx[i]);
    h.i32(u.targetH[i]);
    h.i32(u.targetBuilding[i]);
    h.i32(u.targetWall[i]);
    /**
     * Which stone of the curtain this man is posted to.
     *
     * As behaviour-defining as a field gets: it decides where he steers, how far
     * he can shoot, whether anything on the ground is allowed to touch him, and
     * whether the collision pass will let him onto stone at all. Two worlds
     * identical but for this are two different sieges.
     */
    h.i32(u.wallTile[i]);
    h.num(u.goalX[i]);
    h.num(u.goalY[i]);
    h.u32(u.job[i]);
    h.i32(u.workSite[i]);
    h.num(u.slowT[i]);
    h.num(u.poisonT[i]);
    h.num(u.fearT[i]);
    h.num(u.stunT[i]);
    h.num(u.rallyT[i]);
    h.num(u.healT[i]);
    h.num(u.madnessT[i]);
    h.num(u.shieldT[i]);
    h.num(u.lifeT[i]);
    h.num(u.knockX[i]);
    h.num(u.knockY[i]);
    h.num(u.ammo[i]);
    h.num(u.fatigue[i]);
    /**
     * The basket, and the tree it is being filled from.
     *
     * As behaviour-defining as `workSite`: `carry` is goods that have not yet
     * reached a storehouse and will, `carryKind` decides which bar they land in,
     * `nodeTarget` decides where this man is walking and which node is being
     * worked down, and `haulT` decides when he gives up on a node he cannot
     * reach. Two settlements identical but for these four are two settlements
     * whose next minute of income differs.
     */
    h.num(u.carry[i]);
    h.u32(u.carryKind[i]);
    h.i32(u.nodeTarget[i]);
    h.num(u.haulT[i]);
  }

  // Buildings.
  const b = w.buildings;
  h.u32(b.slots.high);
  h.u32(b.slots.count);
  for (let i = 0; i < b.slots.high; i++) {
    h.u32(b.slots.alive[i]);
    if (b.slots.alive[i] === 0) continue;
    h.u32(b.typeId[i]);
    h.u32(b.tx[i]);
    h.u32(b.ty[i]);
    h.num(b.hp[i]);
    h.num(b.maxHp[i]);
    h.u32(b.team[i]);
    h.num(b.build[i]);
    h.u32(b.workers[i]);
    h.u32(b.tier[i]);
    h.num(b.cooldown[i]);
    h.num(b.stock[i]);
  }

  // Projectiles.
  const p = w.proj;
  h.u32(p.slots.high);
  h.u32(p.slots.count);
  for (let i = 0; i < p.slots.high; i++) {
    h.u32(p.slots.alive[i]);
    if (p.slots.alive[i] === 0) continue;
    h.num(p.x[i]);
    h.num(p.y[i]);
    h.num(p.z[i]);
    h.num(p.vx[i]);
    h.num(p.vy[i]);
    h.num(p.vz[i]);
    h.num(p.ttl[i]);
    h.num(p.damage[i]);
    h.u32(p.dmgType[i]);
    h.u32(p.team[i]);
    h.i32(p.targetH[i]);
  }

  // Companies.
  h.u32(w.companies.length);
  for (const c of w.companies) {
    h.u32(c.id);
    h.u32(c.typeIdx);
    h.u32(c.team);
    h.u32(c.members.length);
    for (const m of c.members) h.i32(m);
    h.u32(c.formation);
    h.u32(c.formationWidth);
    h.u32(c.stance);
    h.num(c.ax);
    h.num(c.ay);
    h.num(c.facing);
    h.num(c.orderX);
    h.num(c.orderY);
    h.u32(c.orderKind);
    // The way round. Empty for an order across open ground, and the difference
    // between a company that walks to the gate and one that walks at the wall
    // when it is not — see `Company.route`.
    h.u32(c.route.length);
    for (const k of c.route) h.i32(k);
    h.num(c.xp);
    h.u32(c.rank);
    h.bool(c.dead);
    h.bool(c.usedSecondWind);
    h.num(c.cohesion);
    // Where this company was told to stand. `stepGarrison` re-reads it every ten
    // ticks to replace the fallen, so a company holding a wall and one that has
    // stood down diverge on the next stride even when every man is in the same
    // place at the moment of hashing.
    h.i32(c.garrisonTile);
  }

  // Pending arrivals.
  h.u32(w.spawnQueue.length);
  for (const s of w.spawnQueue) {
    h.u32(s.typeIdx);
    h.u32(s.count);
    h.i32(s.gate);
    h.num(s.t);
    h.bool(s.boss);
    h.u32(s.flowKind);
  }
  h.str(w.incomingFaction);
  h.num(w.incomingBudget);
  /**
   * What this wave is, and where it comes in.
   *
   * `incomingModifiers` was already simulation-affecting through Blood Moon and
   * The Long Run, and unhashed the whole time — two worlds could differ on
   * whether the horde routs and hash identically. Four more readers just landed
   * (Nightfall, Ash Storm, Famine Wind, Black Rain), so it is now load-bearing
   * for archer range, movement, grain and fire damage as well.
   *
   * `incomingGates` decides which map edges the wave walks in from, which is
   * about as behaviour-defining as a field gets.
   */
  h.u32(w.incomingModifiers.length);
  for (const m of w.incomingModifiers) h.str(m);
  h.u32(w.incomingGates.length);
  for (const g of w.incomingGates) h.i32(g);
  /**
   * The guided run's lesson number. It decides the budget, the faction, the
   * gates and the whole composition of the next five waves (`shapeGuidedWave`),
   * so two worlds that disagree about it are two different runs.
   */
  h.i32(w.tutorialStep);

  // Ground effects.
  h.u32(w.zones.length);
  for (const z of w.zones) {
    h.u32(z.kind);
    h.num(z.x);
    h.num(z.y);
    h.num(z.r);
    h.num(z.t);
    h.num(z.power);
    h.u32(z.team);
  }

  // Progression.
  h.u32(w.unlockedPowers.length);
  for (const p2 of w.unlockedPowers) h.str(p2);
  h.u32(w.relics.length);
  for (const r of w.relics) h.str(r);
  h.u32(w.techNodes.length);
  for (const t of w.techNodes) h.str(t);
  const cdKeys = Object.keys(w.powerCooldowns).sort();
  h.u32(cdKeys.length);
  for (const k of cdKeys) {
    h.str(k);
    h.num(w.powerCooldowns[k]);
  }
  /**
   * Power tiers, and the spoils not yet chosen.
   *
   * `powerTiers` is read by `powerTier()` on every cast — a tiered Starfall
   * costs less Will and lands sooner, so it is as behaviour-defining as the
   * modifier bag. `relicOffer` is a decision the run still owes: `bots.ts`
   * takes one the moment the list is non-empty, and until it does the offer
   * sits in the world. Both sorted by key, because a Record hashed in
   * insertion order is a determinism bug waiting for a load to reorder it.
   */
  const tierKeys = Object.keys(w.powerTiers).sort();
  h.u32(tierKeys.length);
  for (const k of tierKeys) {
    h.str(k);
    h.u32(w.powerTiers[k]);
  }
  h.u32(w.relicOffer.length);
  for (const r of w.relicOffer) h.str(r);

  /**
   * The modifier bag.
   *
   * Every relic and every tech node writes here, and some twenty-five sites
   * across combat, powers, settlement, towers and movement read it — so two
   * worlds with different `mods` are not the same world, however identical
   * everything else looks. This was missing entirely: tripling `unitDamage` or
   * tenfolding `wallHp` left the digest bit-identical, which meant determinism
   * tests, replays and the save round-trip were all blind to the single most
   * behaviour-defining record in the game. The economy made it worse, since
   * `storage`, `grainUpkeep`, `resourceRate` and `reinforceCost` now decide how
   * much a town eats and how fast it earns.
   *
   * Hashed by sorted key rather than as a fixed list, so a modifier added later
   * is covered the moment it exists. Forgetting to update this file is exactly
   * the failure this is guarding against.
   */
  // Bought unit ranks. Sorted, because a Record hashed in insertion order is a
  // determinism bug waiting for a load to reorder it.
  const rankKeys = Object.keys(w.unitRanks).sort();
  h.u32(rankKeys.length);
  for (const k of rankKeys) {
    h.str(k);
    h.u32(w.unitRanks[k]);
  }

  const modKeys = Object.keys(w.mods).sort();
  h.u32(modKeys.length);
  for (const k of modKeys) {
    h.str(k);
    h.num((w.mods as unknown as Record<string, number>)[k]);
  }

  /**
   * The bag with nothing equipped, and the cart that changes it.
   *
   * `modsBase` is hashed *as well as* `mods`, not instead of it, and the
   * distinction is load-bearing rather than belt-and-braces. `mods` is derived
   * from `modsBase` plus the equipped set, so two worlds can agree on the
   * effective bag while disagreeing about what is permanent and what is
   * borrowed — and they diverge the instant either of them takes a sword off.
   * A world one unequip away from a different `unitDamage` is not the same
   * world.
   *
   * The inventory is state for the ordinary reason: an item's level is what its
   * modifiers are computed from, and `nextItemUid` decides the name the next
   * find answers to, which is what every later equip command refers to. Sorted
   * by nothing — the order of the cart is itself state, because that is the
   * order the sheet lists it in and therefore the order the player clicks.
   */
  const baseKeys = Object.keys(w.modsBase).sort();
  h.u32(baseKeys.length);
  for (const k of baseKeys) {
    h.str(k);
    h.num((w.modsBase as unknown as Record<string, number>)[k]);
  }
  h.u32(w.inventory.length);
  for (const s of w.inventory) {
    h.u32(s.uid);
    h.str(s.defId);
    h.u32(s.level);
  }
  h.u32(w.equipped.length);
  for (const uid of w.equipped) h.i32(uid);
  h.u32(w.nextItemUid);
  // The stream the hoard is rolled from. Its cursor is the whole ball game, for
  // the same reason `rngEvent`'s is: two worlds that have rolled a different
  // number of drops hash identically right up until the next one comes out
  // differently.
  h.u32(w.rngSpoils.s);

  /**
   * What the director thinks of you, and the one statistic that feeds it.
   *
   * `profile` is the director's rolling read on how you defend, and it decides
   * what gets bought against you: `spellUse` becomes a `weight` on spell-eaters
   * when the next wave is planned. It is adaptive state, not a report.
   *
   * `powersCast` is the awkward one. It lives in `RunStats`, which is held in a
   * WeakMap *outside* `World` so that hashing stays cheap — but this single
   * counter is read by the director and changes the composition of every wave
   * after it. Two worlds identical in every other hashed field, differing only
   * here, are not the same world: they will be attacked differently. Nothing
   * else in `RunStats` is read by the simulation, so only this one crosses
   * over; if that ever changes, the rest belongs here too.
   */
  const profKeys = Object.keys(w.profile).sort();
  h.u32(profKeys.length);
  for (const k of profKeys) {
    h.str(k);
    h.num((w.profile as unknown as Record<string, number>)[k]);
  }
  h.num(statsOf(w).powersCast);

  // ── The Chronicle ────────────────────────────────────────────────────────
  //
  // The RNG cursor first, and it is the whole ball game. Events are drawn from
  // `rngEvent`; if its cursor were left out, a world that had drawn three
  // events and a world that had drawn none would hash the same right up until
  // the next draw came out differently, and the determinism test would have
  // been passing on a lie the entire time.
  h.u32(w.rngEvent.s);
  // The pending event is *state*: the simulation is frozen while it is set, so
  // whether it is set has to be part of what "this world" means.
  h.bool(w.pendingEvent !== null);
  if (w.pendingEvent) {
    h.str(w.pendingEvent.id);
    h.u32(w.pendingEvent.wave);
    h.u32(w.pendingEvent.choices.length);
    for (const c of w.pendingEvent.choices) h.str(c);
  }
  h.i32(w.lastEventWave);
  // Answers already given. Several of them write to `w.mods` — which is now
  // hashed directly, just above, so this is belt and braces rather than the
  // only cover. It still earns its place: the history is what makes a save
  // remember which answers were already spent.
  h.u32(w.eventHistory.length);
  for (const e of w.eventHistory) h.str(e);
  // A Chronicle choice may re-weight the wave the director has already planned,
  // which made the composition load-bearing rather than merely informational.
  h.u32(w.incomingComposition.length);
  for (const c of w.incomingComposition) {
    h.u32(c.typeIdx);
    h.u32(c.count);
  }

  // The hero. Class, experience, level and which skills were bought all steer
  // the simulation, and so do the battle-skill cooldowns — a cooldown is the
  // reason the next cast did or did not land, which makes it state, not a cache.
  const hero = w.hero;
  h.str(hero.classId);
  h.str(hero.name);
  h.num(hero.xp);
  h.u32(hero.level);
  h.u32(hero.skills.length);
  for (const s of hero.skills) h.str(s);
  const heroCd = Object.keys(hero.cooldowns).sort();
  h.u32(heroCd.length);
  for (const k of heroCd) {
    h.str(k);
    h.num(hero.cooldowns[k]);
  }
  h.i32(hero.unitH);
  h.i32(hero.companyId);
  h.bool(hero.fallen);
  h.num(hero.reviveT);

  // ── The campaign world map ───────────────────────────────────────────────
  // Deliberately last and in one block, so this is a single additive hunk.
  // The campaign's RNG stream is hashed whether or not a campaign is running,
  // because it is a field of every World; the state itself only when there is
  // one. `hashCampaign` covers the province graph too — it is state, not a
  // cache, and a campaign with its roads cut must not hash as one without.
  h.u32(w.rngCampaign.s);
  h.bool(w.campaign !== undefined);
  if (w.campaign) hashCampaign(h, w.campaign);

  // ── Battle mode ──────────────────────────────────────────────────────────
  //
  // `w.battle` is not a mode flag, it is the fork in `stepWorld`: with it set,
  // the settlement, the director and the whole phase clock are replaced. Two
  // worlds that differ on whether it is present are not remotely the same
  // world, and yet the digest could not tell them apart.
  //
  // `deploying` gates targeting, swings, projectiles, towers, powers and
  // morale — half the tick. `outcome` stops `stepBattle` dead. `elapsed` is
  // what the grace window is measured against. The company lists decide who
  // routs when an army breaks, and `scenario` decides whether the enemy
  // advances or holds. `resolve` and `broken` are recomputed every stepped
  // tick, but they are stored fields carried across a save, so they are hashed
  // with the rest rather than left to be a cache that a load could disagree
  // about.
  //
  // Deliberately left out: `name`, which is `faction(factionId).name` and
  // therefore already covered by hashing the id, and `log`, which is
  // player-facing prose that nothing in the simulation ever reads.
  h.bool(w.battle !== undefined);
  if (w.battle) {
    const bt = w.battle;
    h.str(bt.scenario.id);
    // Shared, and hashed: it changes how the field behaves, so both machines
    // must agree they are on one.
    h.bool(bt.pvp);
    h.bool(bt.deploying);
    h.num(bt.elapsed);
    h.i32(bt.outcome);
    h.num(bt.points);
    h.num(bt.deployZone.x0);
    h.num(bt.deployZone.y0);
    h.num(bt.deployZone.x1);
    h.num(bt.deployZone.y1);
    /**
     * Every banner on the field, two in a 1v1 and four in a 2v2.
     *
     * `armies()` returns them in a fixed order — player, enemy, then each ally
     * if present — so this is the same walk it always was for a 1v1 and appends
     * for a 2v2. `team` is hashed because it is what every hostility test in the
     * simulation is taken against, and `routed` because a side that has already
     * run and one that is about to are not the same battle.
     */
    const all = armies(bt);
    h.u32(all.length);
    for (const army of all) {
      h.str(army.factionId);
      h.u32(army.team);
      h.bool(army.routed);
      h.u32(army.broken);
      h.num(army.resolve);
      h.u32(army.companies.length);
      for (const id of army.companies) h.i32(id);
    }
    /**
     * The order of battle the player chose, and the purse they chose it from.
     *
     * Hashed because it *decides the world*: it is what `Cmd.BattleLoadout`
     * writes and what the field is rebuilt out of, so two battles that differ
     * only here are two different armies one edit apart. The order of `picks`
     * is itself state — it is the order the panel lists them in and therefore
     * the order the next click refers to — so it is walked, not sorted.
     */
    // Whether the list may be edited is as much a fact about the battle as what
    // is on it: two otherwise identical fields, one of which the player may
    // re-muster, are not the same battle.
    h.bool(bt.freeMuster);
    h.num(bt.loadout.budget);
    h.u32(bt.loadout.picks.length);
    for (const p of bt.loadout.picks) {
      h.u32(p.typeIdx);
      h.u32(p.count);
    }
    /**
     * And the field itself. Where the cover is decides the fight — obstacles
     * refuse feet, stop arrows and hide the man behind them — so the scatter is
     * as behaviour-defining as the terrain under it. The derived tile bitmap on
     * `World.obstacleGrid` is deliberately *not* hashed, for the same reason the
     * flow fields are not: it is a pure function of this list.
     */
    h.u32(bt.obstacles.length);
    for (const o of bt.obstacles) {
      h.u32(o.kind);
      h.num(o.x);
      h.num(o.y);
      h.num(o.r);
    }
    /**
     * And the relief, for the reason above it: which side has the hill decides
     * the battle. It costs speed to climb, it buys reach to shoot from, and it
     * swings a melee — so two fields whose ridges are in different places are
     * not the same battle however identical the armies on them. `heightGrid` is
     * deliberately not hashed; it is a pure function of this list.
     */
    h.u32(bt.hills.length);
    for (const hl of bt.hills) {
      h.num(hl.x);
      h.num(hl.y);
      h.num(hl.r);
      h.num(hl.h);
    }
  }

  // ── The Long Ledger ──────────────────────────────────────────────────────
  // The date is state, not decoration: the season it lands in multiplies what
  // the fields yield and what the town eats, so two worlds standing in
  // different months are not the same world however identical their stockpiles
  // look. The part-day accumulator matters as much as the day count — without
  // it, a world one tick short of Frostmonth would hash as one already in it.
  h.u32(w.calendarDay);
  h.num(w.calendarT);
  /**
   * And whether this world is holding.
   *
   * `battleHold` is a fork in `stepWorld` exactly as `pendingEvent` is: with it
   * set, nothing happens but the tick counter. A realm that is held while the
   * player is off fighting and a realm that is running are not the same world,
   * and if the digest could not tell them apart, every guarantee about a battle
   * costing you nothing would be unverifiable.
   */
  h.bool(w.battleHold);

  /**
   * ── The trees, the rocks, the ore, and what the player has asked to be
   * mended ─────────────────────────────────────────────────────────────────
   *
   * Deliberately last and in one block, so this is a single additive hunk for
   * whoever merges.
   *
   * The node store is state of the map, not a cache of it. A wood that has been
   * felled is gone: the node is removed, the ground under it reverts to ash
   * plain, the move cost of that tile changes and nothing will ever put it back.
   * `amount` is hashed as well as the count, because a half-worked seam and a
   * fresh one are a different number of minutes of iron. `max` is hashed because
   * a node seeded by an older catalogue and one seeded by this one are different
   * objects even at the same remaining amount, and the difference is what a bar
   * over the stump would draw.
   *
   * `repairOrders` is hashed for the reason `relicOffer` is: it is a decision
   * the player has made that the world has not yet finished acting on. It sends
   * gangs somewhere they would not otherwise go, and it is the only thing in the
   * game that mends a building while a siege is running.
   */
  const rn = w.nodes;
  h.u32(rn.slots.high);
  h.u32(rn.slots.count);
  for (let i = 0; i < rn.slots.high; i++) {
    h.u32(rn.slots.alive[i]);
    if (rn.slots.alive[i] === 0) continue;
    h.u32(rn.slots.gen[i]);
    h.i32(rn.tile[i]);
    h.u32(rn.kind[i]);
    h.num(rn.amount[i]);
    h.num(rn.max[i]);
  }
  h.u32(w.repairOrders.length);
  for (const i of w.repairOrders) h.i32(i);

  return h.digest();
}

export function hashWorldHex(w: World): string {
  return hashWorld(w).toString(16).padStart(8, '0');
}
